/**
 * "O que acontece quando o lead toca": o caminho de cada botao do modelo,
 * escrito em termos de negocio, logo abaixo da previa do aparelho.
 *
 * Por que existe: o numero conectado costuma ser o do COMERCIAL da empresa
 * (ADR-001 E5). Quem toca em "Tenho uma dúvida" cai com a vendedora e a regua
 * PARA na hora para esse lead; se ela fechar em ate 7 dias, a comissao
 * continua creditada a este toque, e a vendedora aparece como "fechado por".
 * Isso precisa estar escrito no editor e na previa, e nao so na cabeca de quem
 * montou a regua -- e o que evita alguem "consertar" a regua para continuar
 * mandando mensagem a quem ja esta conversando com uma pessoa.
 *
 * Tambem cobre quem responde DIGITANDO (sem botao, ou na Evolution): o mesmo
 * caminho do botao de resposta.
 */

import { Icon } from '../../../components/ui';
import type { TemplateAudience, TemplateButton, TemplateChannel } from '../types';

function Caminho({ tom, icone, titulo, children }: { readonly tom: 'link' | 'resposta' | 'neutro'; readonly icone: 'externo' | 'resposta' | 'telefone' | 'email'; readonly titulo: string; readonly children: React.ReactNode }): JSX.Element {
  const estilo = tom === 'link' ? 'border-info/30 bg-info/5' : tom === 'resposta' ? 'border-series-7/30 bg-series-7/5' : 'border-line bg-elevated';
  const cor = tom === 'link' ? 'text-info' : tom === 'resposta' ? 'text-series-7' : 'text-ink-subtle';
  return (
    <li className={`flex items-start gap-2.5 rounded-xl border px-3 py-2 text-xs ${estilo}`}>
      <Icon name={icone} size="xs" className={`mt-0.5 shrink-0 ${cor}`} />
      <p className="min-w-0 text-ink-muted">
        <span className="font-semibold text-ink">{titulo}</span> {children}
      </p>
    </li>
  );
}

const CREDITO = 'A comissão é creditada a este toque (último contato em até 7 dias).';

/**
 * O que a resposta faz com a regua depende de com quem o modelo fala: no toque
 * da regua ela PARA ali; com quem ja esta com a vendedora ela ja estava parada;
 * no pos-venda ela parou com o pagamento. Dizer "a regua para na hora" numa
 * mensagem de pos-venda seria descrever um fluxo que nao existe.
 */
function efeitoNaRegua(audience: TemplateAudience): JSX.Element {
  switch (audience) {
    case 'checkout_abandonado':
    case 'plano_vencido':
      return <strong className="text-ink">a régua para na hora para esse lead</strong>;
    case 'respondeu_regua':
      return <>a régua já está parada para esse lead desde a primeira resposta</>;
    case 'pagou_apos_toque':
      return <>a régua já parou com o pagamento</>;
  }
}

export function DestinoDosBotoes({
  channel,
  buttons,
  audience,
}: {
  readonly channel: TemplateChannel;
  readonly buttons: readonly TemplateButton[];
  readonly audience: TemplateAudience;
}): JSX.Element {
  const credita = audience === 'checkout_abandonado' || audience === 'plano_vencido';
  const posVenda = audience === 'pagou_apos_toque';
  const temResposta = buttons.some((b) => b.kind === 'reply');
  const destinoResposta = posVenda ? (
    <>
      cai no <strong className="text-ink">atendimento da empresa</strong>
    </>
  ) : (
    <>
      cai no WhatsApp do <strong className="text-ink">comercial da empresa</strong>, com a vendedora
    </>
  );
  const sobreOLink = posVenda
    ? 'abre o acesso do lead (área de membros ou recibo). A venda já aconteceu e foi atribuída ao toque anterior: este clique não gera crédito novo.'
    : `abre o checkout do próprio lead, com a marca da régua (o clique é medido). Pagou em até 7 dias = recuperado${credita ? `. ${CREDITO}` : '.'}`;

  return (
    <section aria-label="O que acontece quando o lead toca" className="flex flex-col gap-2">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">O que acontece quando o lead toca</h4>
      <ul className="flex flex-col gap-1.5">
        {channel === 'whatsapp_oficial' || channel === 'email'
          ? buttons.map((b, i) =>
              b.kind === 'link' ? (
                <Caminho key={`${b.label}-${i}`} tom="link" icone="externo" titulo={`“${b.label || `Botão ${i + 1}`}”`}>
                  {sobreOLink}
                </Caminho>
              ) : (
                <Caminho key={`${b.label}-${i}`} tom="resposta" icone="resposta" titulo={`“${b.label || `Botão ${i + 1}`}”`}>
                  {destinoResposta}, e {efeitoNaRegua(audience)}.{' '}
                  {credita ? 'Se a vendedora fechar em até 7 dias, a comissão continua deste toque; ela aparece como “fechado por”.' : null}
                </Caminho>
              ),
            )
          : null}

        {channel === 'whatsapp_oficial' && !temResposta ? (
          <Caminho tom="resposta" icone="resposta" titulo="Se responder digitando:">
            a mensagem {destinoResposta}, e {efeitoNaRegua(audience)}. {credita ? 'O crédito continua neste toque.' : null}
          </Caminho>
        ) : null}

        {channel === 'whatsapp_evolution' ? (
          <>
            <Caminho tom="link" icone="externo" titulo="Link no texto:">
              {posVenda ? `o {link} ${sobreOLink}` : `o {link} passa pelo redirecionador da régua (clique medido) e abre o checkout do lead.${credita ? ` ${CREDITO}` : ''}`}
            </Caminho>
            <Caminho tom="resposta" icone="resposta" titulo="Se responder:">
              texto livre não tem botão; a resposta digitada {destinoResposta}, e {efeitoNaRegua(audience)}.
            </Caminho>
          </>
        ) : null}

        {channel === 'email' ? (
          <Caminho tom="neutro" icone="email" titulo="Se responder o e-mail:">
            a resposta vai para o endereço {posVenda ? 'do atendimento' : 'do comercial'} da empresa, e {efeitoNaRegua(audience)}.
          </Caminho>
        ) : null}

        {channel === 'voice' ? (
          <Caminho tom="resposta" icone="telefone" titulo="Se pedir uma pessoa:">
            a IA registra o pedido, {efeitoNaRegua(audience)} e a vendedora retorna pelo WhatsApp. {credita ? 'Venda em até 7 dias continua creditada a esta ligação.' : null}
          </Caminho>
        ) : null}
      </ul>
      {!credita ? (
        <p className="text-[11px] text-ink-subtle">
          {audience === 'respondeu_regua'
            ? 'Este modelo fala com quem já está com a vendedora: não gera crédito próprio. A comissão fica com o toque da régua que trouxe o lead de volta (ADR-001 E5).'
            : 'Este modelo fala com quem já pagou: não gera crédito, a venda já foi atribuída ao toque anterior.'}
        </p>
      ) : null}
    </section>
  );
}
